import { useLayoutEffect } from 'react';
import { settleDesktopApp } from './desktop-app';
import { parseDesktopRelease } from './storefront/welcome/release';

/**
 * The client's half of a prerendered page (`src/prerender.tsx`, `scripts/prerender.mjs`).
 *
 * A public page such as `/creators` is also written out at build time as static HTML, because the
 * crawlers that feed AI assistants and most link readers do not run a line of script: to them this
 * single-page app is an empty `<div id="root">`. That HTML sits *beside* the root, in
 * `#bf-prerendered`, rather than inside it. React's first commit on a fresh root is the route's
 * loading ground while the page's chunk is fetched, and inside the root that would replace the page
 * a visitor is already reading with a blank one. Beside it, the static page stays on screen, the
 * root is hidden by a rule in the same file while the static page is there, and the static page is
 * taken away in the same frame React commits the real one (`PrerenderedHandoff`).
 *
 * Every visitor gets the same HTML, people and crawlers alike, and it is the page they are about to
 * see rendered by the same components: nothing is served by user agent.
 */

export const PRERENDERED_ID = 'bf-prerendered';
export const PRERENDERED_STATE_ID = 'bf-prerendered-state';

/** What the build knew when it rendered the page, so the first live render says the same thing. */
export interface PrerenderedState {
  /** The desktop release manifest as the build read it, or null for none. */
  release: unknown;
}

/**
 * Prime what the page was rendered with, before the first render.
 *
 * The desktop release decides whole paragraphs of the public pages, and the live manifest arrives a
 * moment after the first render. Without this, a prerendered page that says "get BoardFlight for
 * your computer" would turn into the no-app version for that moment and back again. The live
 * manifest is still asked for as usual and settles the answer again when it arrives, so a release
 * made after the build is shown as soon as it is known.
 */
export function primePrerendered(): void {
  if (typeof document === 'undefined') return;
  const element = document.getElementById(PRERENDERED_STATE_ID);
  if (!element?.textContent) return;
  try {
    const state = JSON.parse(element.textContent) as PrerenderedState;
    settleDesktopApp(parseDesktopRelease(state.release));
  } catch {
    // A state that does not parse is a page rendered by some other build. The live manifest decides.
  }
}

/** Take the static page away. Idempotent, and a no-op on a page that was never prerendered. */
export function releasePrerendered(): void {
  if (typeof document === 'undefined') return;
  document.getElementById(PRERENDERED_ID)?.remove();
}

/**
 * Rendered beside the routes, inside the same Suspense boundary: it commits only once the routed
 * page has, and a layout effect runs before that frame is painted, so the static page and the live
 * one swap without a blank frame between them.
 */
export function PrerenderedHandoff(): null {
  useLayoutEffect(releasePrerendered, []);
  return null;
}
